import logo from "../../assets/images/logo-vektorski-1.png";
import logoElmap from "../../assets/images/elmap-logo-transparent.png";

function drawWrappedText(ctx, text, x, y, maxWidth, lineHeight) {
  if (!text) return y;

  const words = String(text).split(" ");
  let line = "";

  for (let n = 0; n < words.length; n++) {
    const testLine = line ? `${line} ${words[n]}` : words[n];
    const metrics = ctx.measureText(testLine);

    if (metrics.width > maxWidth && n > 0) {
      ctx.fillText(line, x, y);
      line = words[n];
      y += lineHeight;
    } else {
      line = testLine;
    }
  }

  if (line) {
    ctx.fillText(line, x, y);
    y += lineHeight;
  }

  return y;
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function normalizeSelectedFeatures(selectedFeature) {
  if (!selectedFeature) return [];
  if (Array.isArray(selectedFeature)) return selectedFeature.flat().filter(Boolean);
  return [selectedFeature];
}

function createPrintSizedMapCanvas(sourceCanvas) {
  const outW = 1800;
  const outH = 2300;

  const srcW = sourceCanvas.width;
  const srcH = sourceCanvas.height;

  const outCanvas = document.createElement("canvas");
  outCanvas.width = outW;
  outCanvas.height = outH;

  const outCtx = outCanvas.getContext("2d");
  outCtx.fillStyle = "white";
  outCtx.fillRect(0, 0, outW, outH);

  const scale = Math.max(outW / srcW, outH / srcH);
  const drawW = srcW * scale;
  const drawH = srcH * scale;
  const dx = (outW - drawW) / 2;
  const dy = (outH - drawH) / 2;

  outCtx.drawImage(sourceCanvas, dx, dy, drawW, drawH);

  return outCanvas;
}

function pageHeader(title) {
  return `
    <header class="print-header">
      <img src="${logo}" class="logo-left" />
      <div class="head-meta">
        <div class="title">${escapeHtml(title)}</div>
      </div>
      <img src="${logoElmap}" class="logo-right" />
    </header>
  `;
}

function pageFooter(pageNumber, totalPages) {
  return `
    <footer class="print-footer">
      ${pageNumber}/${totalPages}
    </footer>
  `;
}

function getLayerUrl(layer) {
  return layer?.props?.data || "";
}

function getRawLayerId(layer) {
  return layer?.props?.id || layer?.id || "";
}

function parseLayerNameLikeLayerPanel(id) {
  if (!id) return "";

  const parts = String(id).split("-");
  return parts.length > 1 ? parts[0] : String(id);
}

function cleanLayerTitle(value) {
  return String(value || "")
    .replace(/-layer$/i, "")
    .replace(/-\d+$/i, "")
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}

function getLegendLayerTitle(layer, index) {
  return cleanLayerTitle(
    layer?.props?.title ||
    layer?.props?.name ||
    layer?.props?.id ||
    layer?.id ||
    `WMS sloj ${index + 1}`
  );
}

function buildWmsLegendUrl(layer) {
  const layerUrl = getLayerUrl(layer);
  const rawLayerId = getRawLayerId(layer);
  const parsedLayerName = parseLayerNameLikeLayerPanel(rawLayerId);

  if (!layerUrl || !parsedLayerName) return null;

  const separator = layerUrl.includes("?") ? "&" : "?";

  return (
    `${layerUrl}${separator}` +
    `service=WMS` +
    `&version=1.1.1` +
    `&request=GetLegendGraphic` +
    `&format=image/png` +
    `&layer=${parsedLayerName}` +
    `&transparent=true`
  );
}

function buildLegendHtml(layers, totalPages, pageNumber) {
  const wmsLegendLayers = Array.isArray(layers)
    ? layers
      .filter(Boolean)
      .map((layer, index) => ({
        index,
        title: getLegendLayerTitle(layer, index),
        legendUrl: buildWmsLegendUrl(layer),
      }))
      .filter((item) => item.legendUrl)
    : [];

  if (!wmsLegendLayers.length) return "";

  const legends = wmsLegendLayers
    .map(
      ({ title, legendUrl }) => `
        <div class="legend-item">
          <div class="legend-layer-title">${escapeHtml(title)}</div>
          <img
            src="${legendUrl}"
            class="legend-img"
            alt="Legenda za ${escapeHtml(title)}"
          />
        </div>
      `
    )
    .join("");

  return `
    <section class="page">
      ${pageHeader("Legenda")}

      <main class="print-content legend-content">
        ${legends}
      </main>

      ${pageFooter(pageNumber, totalPages)}
    </section>
  `;
}

function buildSelectedFeatureHtml(selectedFeature, totalPages, startPageNumber) {
  const features = normalizeSelectedFeatures(selectedFeature);

  if (!features.length) return "";

  return features
    .map((feature, index) => {
      const props = feature?.properties || {};
      const pageNumber = startPageNumber + index;

      const rows = Object.entries(props)
        .map(
          ([key, value]) => `
            <tr>
              <th>${escapeHtml(key)}</th>
              <td>${escapeHtml(value)}</td>
            </tr>
          `
        )
        .join("");

      return `
        <section class="page">
          ${pageHeader(`Odabrani element ${index + 1}`)}

          <main class="print-content">
            <h3 class="feature-title">
              ${escapeHtml(feature?.id || "Bez ID-a")}
            </h3>

            ${rows
          ? `
                  <table class="attr-table" cellspacing="0" cellpadding="0">
                    <tbody>${rows}</tbody>
                  </table>
                `
          : `<p class="empty-message">Nema izabranih atributa za ovaj objekat.</p>`
        }
          </main>

          ${pageFooter(pageNumber, totalPages)}
        </section>
      `;
    })
    .join("");
}

function hasWmsLegend(layers) {
  return Array.isArray(layers) && layers.some((layer) => buildWmsLegendUrl(layer));
}

function buildPrintHtml({ mapDataUrl, selectedFeature, layers }) {
  const features = normalizeSelectedFeatures(selectedFeature);
  const hasLegend = hasWmsLegend(layers);

  const totalPages = 1 + (hasLegend ? 1 : 0) + features.length;
  const legendPageNumber = hasLegend ? 2 : null;
  const firstFeaturePageNumber = hasLegend ? 3 : 2;

  const legendHtml = hasLegend
    ? buildLegendHtml(layers, totalPages, legendPageNumber)
    : "";

  const selectedHtml = buildSelectedFeatureHtml(
    selectedFeature,
    totalPages,
    firstFeaturePageNumber
  );

  return `
<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Štampa plana</title>

  <style>
    @page {
      size: A4 portrait;
      margin: 12mm;
    }

    html,
    body {
      margin: 0;
      padding: 0;
      background: white;
      font-family: Arial, Helvetica, sans-serif;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .page {
      width: 100%;
      height: calc(297mm - 24mm);
      box-sizing: border-box;
      display: grid;
      grid-template-rows: 18mm 1fr 8mm;
      break-after: page;
      page-break-after: always;
      background: white;
    }

    .page:last-child {
      break-after: auto;
      page-break-after: auto;
    }

    .print-header {
      display: grid;
      grid-template-columns: 38mm 1fr 38mm;
      align-items: center;
      gap: 8mm;
      border-bottom: 1px solid #d1d5db;
      padding-bottom: 4mm;
      box-sizing: border-box;
    }

    .logo-left {
      width: 32mm;
      height: auto;
      display: block;
      justify-self: start;
    }

    .logo-right {
      width: 32mm;
      height: auto;
      display: block;
      justify-self: end;
    }

    .head-meta {
      display: flex;
      justify-content: center;
      align-items: center;
      text-align: center;
    }

    .head-meta .title {
      font-weight: bold;
      font-size: 15px;
      line-height: 1.2;
    }

    .print-content {
      min-height: 0;
      padding-top: 5mm;
      box-sizing: border-box;
      overflow: hidden;
    }

    .map-wrapper {
      width: 100%;
      height: 100%;
      box-sizing: border-box;
      overflow: hidden;
    }

    .map-img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center center;
    }

    .print-footer {
      border-top: 1px solid #e5e7eb;
      padding-top: 2mm;
      box-sizing: border-box;
      font-size: 11px;
      color: #555;
      text-align: right;
      align-self: end;
    }

    .feature-title {
      margin: 0 0 4mm 0;
      font-size: 14px;
    }

    .attr-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 12px;
    }

    .attr-table th {
      background: #f8fafc;
      width: 35%;
      text-align: left;
      padding: 4px 6px;
      border-bottom: 1px solid #e5e7eb;
      vertical-align: top;
    }

    .attr-table td {
      padding: 4px 6px;
      border-bottom: 1px solid #e5e7eb;
      vertical-align: top;
    }

    .legend-content {
      overflow: visible;
      padding-top: 5mm;
    }

    .legend-item {
      margin-bottom: 8mm;
      page-break-inside: avoid;
      break-inside: avoid;
    }

    .legend-layer-title {
      font-size: 12px;
      font-weight: bold;
      margin-bottom: 2mm;
      color: #111827;
    }

    .legend-img {
      display: block;
      width: auto;
      max-width: 100%;
      height: auto;
      object-fit: contain;
      object-position: left top;
    }

    .empty-message {
      font-size: 13px;
      margin: 0;
    }

    @media print {
      html,
      body {
        width: auto;
        height: auto;
      }
    }
  </style>
</head>

<body>
  <section class="page">
    ${pageHeader("Mapa")}

    <main class="print-content">
      <div class="map-wrapper">
        <img src="${mapDataUrl}" class="map-img" />
      </div>
    </main>

    ${pageFooter(1, totalPages)}
  </section>

  ${legendHtml}

  ${selectedHtml}

  <script>
    window.onload = function () {
      const images = Array.from(document.images);

      Promise.all(
        images.map((img) => {
          if (img.complete) return Promise.resolve();

          return new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
          });
        })
      ).then(() => {
        window.print();
        window.onafterprint = function () {
          window.close();
        };
      });
    };
  </script>
</body>
</html>
  `;
}

export const handlePrint = async ({
  selectedFeature,
  scale,
  userInfo,
  mapRef,
  deckRef,
  layers
}) => {
  const mapCanvas = mapRef?.current?.getMap?.()?.getCanvas?.();
  const deckCanvas = deckRef?.current?.deck?.canvas;

  if (!scale) {
    alert("Razmjera nije definisana.");
    return;
  }

  if (!mapCanvas || !deckCanvas) {
    alert("Canvas nije spreman za štampu.");
    return;
  }

  await new Promise((resolve) => requestAnimationFrame(resolve));

  const mergedCanvas = document.createElement("canvas");
  mergedCanvas.width = mapCanvas.width;
  mergedCanvas.height = mapCanvas.height;

  const mergedCtx = mergedCanvas.getContext("2d");

  mergedCtx.clearRect(0, 0, mergedCanvas.width, mergedCanvas.height);
  mergedCtx.drawImage(mapCanvas, 0, 0);
  mergedCtx.drawImage(deckCanvas, 0, 0);

  const canvas = createPrintSizedMapCanvas(mergedCanvas);
  const ctx = canvas.getContext("2d");

  try {
    const padding = 35;
    const boxX = 45;
    const boxY = 45;
    const boxW = 470;
    const boxH = 250;

    ctx.fillStyle = "white";
    ctx.globalAlpha = 0.9;
    ctx.fillRect(boxX, boxY, boxW, boxH);
    ctx.globalAlpha = 1;

    const logoImage = new Image();
    logoImage.src = logo;

    await new Promise((resolve) => {
      logoImage.onload = resolve;
      logoImage.onerror = resolve;
    });

    ctx.drawImage(logoImage, boxX + padding, boxY + 25, 250, 70);

    const today = new Date().toLocaleDateString("bs-BA");
    const textX = boxX + padding;
    const maxTextWidth = boxW - padding * 2;
    const lineHeight = 26;

    ctx.fillStyle = "black";
    ctx.textAlign = "start";
    ctx.font = "20px Arial";

    let y = boxY + 135;

    y = drawWrappedText(
      ctx,
      `Korisnik: ${userInfo?.name || ""} ${userInfo?.surname || ""}`.trim(),
      textX,
      y,
      maxTextWidth,
      lineHeight
    );

    y = drawWrappedText(
      ctx,
      `Terenska jedinica: ${userInfo?.unit || ""}`,
      textX,
      y,
      maxTextWidth,
      lineHeight
    );

    y = drawWrappedText(
      ctx,
      `Datum: ${today}`,
      textX,
      y,
      maxTextWidth,
      lineHeight
    );

    y = drawWrappedText(
      ctx,
      `Razmjera: ${scale}`,
      textX,
      y,
      maxTextWidth,
      lineHeight
    );
  } catch (error) {
    console.warn("Print overlay warning:", error);
  }

  const img = canvas.toDataURL("image/png", 1.0);

  const printWindow = window.open("", "_blank");

  if (!printWindow) {
    alert("Popup za štampu je blokiran.");
    return;
  }

  printWindow.document.open();
  printWindow.document.write(
    buildPrintHtml({
      mapDataUrl: img,
      selectedFeature,
      layers
    })
  );
  printWindow.document.close();
};